import { useEffect } from 'react';
import { notify } from '@/features/notifications/toastApi';
import { voiceControls } from '@/features/voice/session/voiceControls';
import { voicePrefs } from '@/features/voice/store/voicePrefs';
import { voiceSessionStore } from '@/features/voice/store/voiceSessionStore';
import { activeMediaBinding } from '../activeMedia';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * The desktop shell's hotkeys, applied to whatever this document is holding.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ THE GLOBAL KEY CAN ONLY MUTE. NOTHING THAT ARRIVES FROM AN ACCELERATOR    ║
 * ║ MAY OPEN A MICROPHONE — INCLUDING AN OLDER SHELL'S `toggle-mute`, WHICH   ║
 * ║ IS DOWNGRADED TO A MUTE HERE. SEE `MIC_STATE_FOR`.                        ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Global push-to-talk was removed six rounds ago — `globalShortcut` reports
 * key-DOWN only, so the shell had to infer the release from timing, and six
 * versions of that inference shipped six hot microphones. Push-to-talk itself
 * is untouched and lives in `features/voice/hooks/useVoiceInput.ts`, where the
 * DOM gives it a real `keyup`, a `blur` and an unmount teardown.
 *
 * ── WHY THE SURVIVING KEY STOPPED BEING A TOGGLE ───────────────────────────
 *
 * The seventh hot microphone was on the line that read
 * `setMicEnabled(!voice.micEnabled)`: correct per event, wrong per PRESS,
 * because the OS re-delivers a held accelerator and an even number of flips
 * left the user live. The shell then collapsed a held key to one event, which
 * genuinely worked — and the eighth hot microphone was in that fix, because a
 * DROPPED event over a toggle does not mean "nothing happened", it means "the
 * microphone stayed where the last press left it". Two deliberate presses
 * inside a second — unmute, say one word, mute — and the second was discarded
 * in silence while the room kept listening.
 *
 * Both of those are questions about HOW MANY events arrived. A mute-only key
 * has no such question:
 *
 *     N events = one event.   Zero events -> still muted.   Out of order -> muted.
 *
 * So this hook does not count, does not remember, and does not have to be
 * robust to N. It is not robust to N because someone was careful; it is robust
 * because the operation it performs is idempotent.
 *
 * ── AND MUTING MUTES EVERYTHING THIS DOCUMENT HOLDS ────────────────────────
 *
 * `activeMedia.ts` has a tie-break — a registered meeting/call binding beats
 * the voice channel — because an UNMUTE must land in exactly one room, and it
 * must be the room on screen. A MUTE has no such constraint and should not
 * pretend to: the reason the tie-break exists at all is that a voice channel
 * can be connected in a document where nothing renders its controls (navigate
 * from a voice channel into a meeting and `VoiceRuntime` unmounts while the
 * session stays connected). Under a toggle, one press of the global key
 * UNMUTED that invisible room. Under mute-only it can only close it — so the
 * key now shuts it up instead of opening it, which is the one thing a person in
 * another application actually wants from a key called mute.
 *
 * ── THE RULE FOR NOTICES ───────────────────────────────────────────────────
 *
 * Every round of this saga fixed a microphone and shipped a new defect in the
 * code it wrote to ANNOUNCE the fix — ~31 toasts a second, a false "you held
 * the key", 83 identical toasts for one press, two toasts in two windows for
 * one press, and a notice that was DELETED in exactly the configuration a
 * global hotkey exists for. So:
 *
 *   AT MOST ONE PER PHYSICAL PRESS — one press is one event (the shell), one
 *     event is at most one `note()` here, and every notice carries `NOTICE_ID`
 *     so a second replaces the first rather than stacking. The id matters
 *     because the shell and this bundle ship apart.
 *   DEFERRED, NEVER DELETED — see `note`. The old rule dropped any notice
 *     raised while the window was not on screen, and `closeToTray` defaults to
 *     true, so the one notice this feature exists for was switched off in its
 *     main configuration while the settings card promised the opposite.
 *   ONLY WHAT IS TRUE OF THIS WINDOW — every sentence below is composed from
 *     what THIS document holds. The shell says why it could not route the key;
 *     it does not get to describe a microphone it cannot see.
 */

/**
 * ONE TOAST IDENTITY FOR THE WHOLE FEATURE. Sonner replaces a toast that reuses
 * an id, so N notices are one notice however many arrive — a property of this
 * code rather than of whichever shell version is on the other side of the
 * bridge.
 */
const NOTICE_ID = 'global-mute-key';

/**
 * THE ONLY PLACE A HOTKEY BECOMES A MICROPHONE STATE, AND IT IS A LOOKUP.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ EVERY VERB AN ACCELERATOR CAN PRODUCE MAPS TO `false`. THAT IS THE WHOLE  ║
 * ║ INVARIANT, AND IT IS ONE TABLE RATHER THAN AN ARGUMENT ABOUT TIMING.      ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 *   mute         the global key. The shell cannot express anything else on an
 *                accelerator — `GlobalHotkeyEvent`, apps/desktop/src/main/hotkeys.ts.
 *   toggle-mute  AN OLDER SHELL, DOWNGRADED. The desktop app and this bundle
 *                ship apart, so an installed shell from before this change goes
 *                on sending a toggle. Reading it as a MUTE is what makes the
 *                invariant hold for every shell version rather than only the
 *                current one: previous rounds could only ever say "an
 *                already-installed older shell is unfixable from here".
 *   unmute       THE TRAY'S OWN ITEM, and nothing else. It is a control the
 *                user opened, whose label is the state it is about, and which
 *                they must look at to click. No accelerator can produce it.
 *
 * A verb this table does not know — `toggle-window`, or anything a future shell
 * adds — does nothing here. That is deliberate: an unknown media verb must not
 * fall through to a default, and the only safe default would be `false` anyway.
 */
const MIC_STATE_FOR: Readonly<Record<string, boolean>> = {
  mute: false,
  'toggle-mute': false,
  unmute: true,
};

/** A deferred notice never expires unless it describes a passing condition. */
const NEVER_STALE = Number.POSITIVE_INFINITY;

/**
 * How long a notice about a call that is still CONNECTING is worth showing.
 * The shell's own claim is bounded at ten seconds (`MEDIA_CLAIM_MS`), so after
 * that the sentence is not merely old, it is untrue.
 */
const CONNECTING_NOTICE_MS = 10_000;

interface DeferredNotice {
  readonly message: string;
  readonly description: string;
  /** After this instant the sentence is no longer true; drop it silently. */
  readonly staleAt: number;
}

/**
 * THE ONE NOTICE WAITING FOR THE USER TO COME BACK — at most one, ever.
 *
 * This is the only state in this file, and it is deliberately not the kind of
 * state that has been wrong six times: it can produce a TOAST and nothing else.
 * It holds no microphone, it is not consulted by any media path, and losing it
 * costs a sentence. Bounded at one entry by construction, so it can never
 * become the queue that surfaced 83 toasts a minute after the key press.
 */
let deferred: DeferredNotice | null = null;

/**
 * IS THE USER LOOKING AT THIS WINDOW RIGHT NOW?
 *
 * `document.hasFocus()` and not `visibilityState`, for two reasons that both
 * bit the previous round:
 *
 *   • The workspace window disables `backgroundThrottling`, which the Electron
 *     documentation notes "also affects the Page Visibility API" —
 *     `visibilityState` is pinned at `visible` for the life of that window, so
 *     it can never report the tray at all.
 *   • Chromium's native window occlusion is ON by default in Electron 33 and
 *     this app does not disable it, so a window merely COVERED by the
 *     application the user is working in can report itself hidden.
 *
 * Focus answers the question that actually matters — "will they read this
 * now?" — and being wrong about it only ever changes WHEN a notice appears,
 * never WHETHER it does. When there is no `document` at all (a non-DOM import),
 * the answer is "show it": never delete a notice because of a missing API.
 */
function looking(): boolean {
  if (typeof document === 'undefined') return true;
  if (typeof document.hasFocus !== 'function') return true;
  return document.hasFocus();
}

/**
 * SAY IT NOW IF THEY CAN SEE IT, OTHERWISE SAY IT WHEN THEY COME BACK.
 *
 * NEVER DROP IT. The previous rule returned here without creating the toast
 * whenever the window was off screen — which is the shipped default and the
 * only configuration a global hotkey exists for, so the notice was deleted
 * exactly when it mattered and the settings card said the opposite. A notice
 * that arrives late is a notice; a notice that never arrives is silence, and
 * for a mute key silence means "you may still be on air and nobody told you".
 */
function note(message: string, description: string, ttlMs = NEVER_STALE): void {
  if (looking()) {
    notify.warning(message, description, { id: NOTICE_ID });
    return;
  }
  deferred = {
    message,
    description,
    staleAt: ttlMs === NEVER_STALE ? NEVER_STALE : Date.now() + ttlMs,
  };
}

/** The user came back. Say the one thing we were holding, if it is still true. */
function flushDeferred(): void {
  const pending = deferred;
  deferred = null;
  if (pending === null) return;
  if (Date.now() > pending.staleAt) return;
  notify.warning(pending.message, pending.description, { id: NOTICE_ID });
}

/** Is a microphone this document owns live right now? */
function liveHere(): boolean {
  if (activeMediaBinding() !== null) return true;
  const voice = voiceSessionStore.get();
  return voice.status === 'connected' && voice.client !== null;
}

/**
 * MUTE EVERY ROOM THIS DOCUMENT HOLDS. Returns whether there was one.
 *
 * BOTH, not the tie-break's winner. See the header: the tie-break in
 * activeMedia.ts exists so that an UNMUTE lands in the room on screen, and its
 * losing side is precisely the room that has no visible controls — which is the
 * room a person pressing mute from another application most needs closed.
 * Closing more than they asked for is never the failure this feature has.
 */
function muteEverythingHere(): boolean {
  let acted = false;

  const binding = activeMediaBinding();
  if (binding !== null) {
    binding.setMicEnabled(false);
    acted = true;
  }

  const voice = voiceSessionStore.get();
  if (voice.status === 'connected' && voice.client !== null) {
    void voiceControls.setMicEnabled(false);
    acted = true;
  }

  return acted;
}

/**
 * UNMUTE THE ROOM ON SCREEN. Returns whether there was one. TRAY ONLY.
 *
 * THE ROOM ON SCREEN FIRST. A binding exists only while a live meeting or call
 * room is mounted, and those routes are outside `AppShell` — so a binding means
 * the voice dock is not rendered and the meeting is what the user is looking
 * at. Asking the voice channel first would open a room they cannot see, in a
 * document where the only microphone glyph belongs to the meeting and would not
 * move. See ../activeMedia.ts.
 *
 * EXACTLY ONE, unlike the mute above, and that asymmetry is the same rule
 * `voiceControls.ts` states at the top of its own file: an early return may
 * decline to OPEN a microphone and may never decline to CLOSE one.
 */
function unmuteRoomOnScreen(): boolean {
  const binding = activeMediaBinding();
  if (binding !== null) {
    binding.setMicEnabled(true);
    return true;
  }

  const voice = voiceSessionStore.get();
  if (voice.status === 'connected' && voice.client !== null) {
    void voiceControls.setMicEnabled(true);
    return true;
  }

  return false;
}

/**
 * THE CASE WHERE THE OS MICROPHONE LIGHT IS HONEST AND STILL MISLEADING.
 *
 * The light says CAPTURING, and in a voice channel there are two states where
 * capturing is not the same as being heard:
 *
 *   PUSH TO TALK — `setMicEnabled(true)` computes
 *     `transmitting = !deafened && inputMode !== 'push-to-talk'`, which for a
 *     push-to-talk user is ALWAYS false. Capture is restored and the published
 *     track stays muted.
 *   DEAFENED — deafen promises "silence everyone AND stop transmitting", so an
 *     unmute while deafened is refused at the published track by design.
 *
 * Both are legitimate refusals to OPEN — the rule in voiceControls.ts — and
 * both are invisible. This is now reachable only from the TRAY, because the
 * global key cannot unmute at all, and the tray user is by definition at their
 * machine — so the notice usually lands immediately rather than deferred.
 *
 * ONLY ON THE UNMUTE HALF. Muting needs no notice: the light going dark is the
 * notice, and it is a better one than this.
 */
function announceUnheard(deafened: boolean): void {
  if (deafened) {
    note(
      'Microphone on, but you are deafened',
      'Nothing reaches the room until you undeafen in Cyphex Teams.',
    );
    return;
  }
  if (voicePrefs.get().inputMode !== 'push-to-talk') return;
  note(
    'Microphone on — hold your push-to-talk key',
    'Push to talk is on, so the room hears you only while you hold your key in the Cyphex Teams window.',
  );
}

export function useDesktopHotkeys(): void {
  /**
   * A GLOBAL MEDIA HOTKEY ARRIVED AND THE SHELL HAD NOWHERE TO ROUTE IT.
   *
   * THE REASON IS READ FIRST. It used to be read second, behind `liveHere()`,
   * so the "another window is opening a call" sentence was composed for every
   * reason including `no-window`, where there is no other window at all.
   *
   * EVERY BRANCH LEADS WITH THE SAME FACT — NOTHING WAS MUTED — because that is
   * what `media-hotkey` means and it is the only thing the user needs from a
   * mute key that did not fire. The previous copy said "the key went there",
   * which is false 100% of the time it is shown: the shell raises this event
   * ONLY when the key went nowhere.
   */
  useDesktopEvent('media-hotkey', (event) => {
    /* THE REASON CHOOSES THE BRANCH; WHAT THIS WINDOW HOLDS CHOOSES THE
       SENTENCE. Four cases, four sentences, and each one is true of exactly one
       of them — which is what round 8 had two of, in the wrong order. */
    if (event.reason === 'no-window') {
      note(
        'Nothing was muted',
        liveHere()
          ? 'Cyphex Teams has not registered this window’s microphone yet. Use the microphone button in this window.'
          : 'You are not in a call or a voice channel in this window.',
      );
      return;
    }
    /* 'connecting' — and also an older shell that sends no reason at all, which
       only ever raised this event for a call window that was connecting. */
    note(
      'Nothing was muted',
      liveHere()
        ? 'A call window is opening and it holds the microphone key until it connects. Use the microphone button in this window if you need to mute it now.'
        : 'The call is still connecting, so there is no microphone to mute yet.',
      CONNECTING_NOTICE_MS,
    );
  });

  useDesktopEvent('hotkey', (event) => {
    const wanted = MIC_STATE_FOR[event.action];
    /* `toggle-window`, and any verb a future shell invents. Not a media event
       and deliberately not defaulted into one. */
    if (wanted === undefined) return;

    if (wanted) {
      /* THE TRAY. Nothing else can reach this branch — see `MIC_STATE_FOR`. */
      if (!unmuteRoomOnScreen()) {
        note('Nothing to unmute', 'You are not in a call or a voice channel in this window.');
        return;
      }
      const voice = voiceSessionStore.get();
      if (activeMediaBinding() === null && voice.status === 'connected') {
        announceUnheard(voice.deafened);
      }
      return;
    }

    if (muteEverythingHere()) {
      /* NO NOTICE, DELIBERATELY. Capture stopped, so the operating system's own
         microphone indicator goes dark — an affordance this process cannot lie
         about, visible from the application the user is actually looking at,
         which no toast in this document is. */
      return;
    }

    /* NOT SILENT. The shell routed this here because this document is the one
       holding live media, or because it is the workspace and nobody else
       claimed any — and in the second case there may be nothing here at all.
       For a MUTE key that difference matters more than it used to: a user who
       believes they pressed mute and did not is a user who is still on air. */
    note('Nothing was muted', 'You are not in a call or a voice channel in this window.');
  });

  /**
   * THE DEFERRED NOTICE, DELIVERED WHEN THE USER COMES BACK.
   *
   * `focus` on the window and `visibilitychange` on the document, because
   * neither one alone covers this app: the workspace window pins
   * `visibilityState` at `visible` (it disables `backgroundThrottling`), and a
   * call window restored from the taskbar may fire only the visibility change.
   * Both funnel into the same one-entry flush, so two triggers cannot produce
   * two toasts.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') flushDeferred();
    };
    window.addEventListener('focus', flushDeferred);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', flushDeferred);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
